import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtTL, fmtMoney, CURRENCY_OPTIONS, todayIso, dateInput } from "@/lib/format";
import { Plus, Trash2, AlertTriangle, FileDown } from "lucide-react";
import { toast } from "sonner";

const UNITS = ["Adet", "KG", "Litre", "Koli", "Paket", "Kutu", "Metre", "Rulo", "Palet"];

export default function QuoteForm({ mode = "quote" }) {
  // mode: "quote" or "order"
  const isOrder = mode === "order";
  const { id } = useParams();
  const isEdit = !!id && !isOrder;
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [saving, setSaving] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const preCompany = params.get("company") || "";
  const preQuote = params.get("from_quote") || "";

  const [form, setForm] = useState({
    company_id: preCompany,
    manual_company_name: "",
    quote_date: todayIso(),
    valid_until: dateInput(new Date(Date.now() + 30 * 86400000)),
    order_date: todayIso(),
    contact_person: "",
    phone: "",
    email: "",
    contact_title: "",
    delivery_time: "5 iş günü",
    payment_term_days: 30,
    notes: "",
    discount_amount: 0,
    status: "draft",
    currency: "TRY",
  });
  const [items, setItems] = useState([
    { product_id: "", manual_product_name: "", product_code: "", quantity: 1, unit: "Adet", unit_price: 0 },
  ]);

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data));
    api.get("/products", { params: { is_active: true } }).then((r) => setProducts(r.data));
    api.get("/definitions/categories").then((r) => setCategories(r.data));
    api.get("/definitions/brands").then((r) => setBrands(r.data));
    if (isEdit) {
      api.get(`/quotes/${id}`).then((r) => {
        const q = r.data;
        setForm({
          company_id: q.company_id || "",
          manual_company_name: q.manual_company_name || "",
          quote_date: dateInput(q.quote_date),
          valid_until: dateInput(q.valid_until),
          order_date: dateInput(q.quote_date),
          contact_person: q.contact_person,
          phone: q.phone,
          email: q.email,
          contact_title: q.contact_title,
          delivery_time: q.delivery_time,
          payment_term_days: q.payment_term_days,
          notes: q.notes || "",
          discount_amount: q.discount_amount || 0,
          status: q.status,
        });
        setItems(q.items.map((it) => ({
          product_id: it.product_id || "",
          manual_product_name: it.manual_product_name || "",
          product_code: it.product_code || "",
          quantity: it.quantity,
          unit: it.unit,
          unit_price: it.unit_price,
        })));
      });
    }
    if (preQuote && isOrder) {
      // Pre-populate from quote
      api.get(`/quotes/${preQuote}`).then((r) => {
        const q = r.data;
        setForm((f) => ({
          ...f,
          company_id: q.company_id || "",
          contact_person: q.contact_person,
          phone: q.phone,
          email: q.email,
          contact_title: q.contact_title,
          delivery_time: q.delivery_time,
          payment_term_days: q.payment_term_days,
          notes: q.notes || "",
        }));
        setItems(q.items.filter(it => it.product_id).map((it) => ({
          product_id: it.product_id,
          quantity: it.quantity,
          unit: it.unit,
          unit_price: it.unit_price,
          manual_product_name: "",
          product_code: it.product_code,
        })));
      });
    }
  }, [id, isEdit, isOrder, preQuote]);

  useEffect(() => {
    if (form.company_id && companies.length > 0) {
      const c = companies.find((x) => x.id === form.company_id);
      if (c && !form.contact_person) {
        setForm((f) => ({
          ...f, contact_person: c.contact_person, phone: c.mobile_phone, email: c.email,
          payment_term_days: c.payment_term_days,
        }));
      }
    }
  }, [form.company_id, companies]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setItem = (idx, k, v) => {
    setItems((arr) => {
      const next = [...arr];
      next[idx] = { ...next[idx], [k]: v };
      if (k === "product_id" && v) {
        const p = products.find((x) => x.id === v);
        if (p) {
          next[idx].product_code = p.product_code;
          next[idx].unit = p.unit;
          if (p.calculated_sales_price) {
            next[idx].unit_price = p.calculated_sales_price;
          }
          next[idx].manual_product_name = "";
        }
      }
      return next;
    });
  };

  const addRow = () => setItems((a) => [...a, {
    product_id: "", manual_product_name: "", product_code: "",
    quantity: 1, unit: "Adet", unit_price: 0,
  }]);
  const removeRow = (i) => setItems((a) => a.filter((_, idx) => idx !== i));

  const subtotal = items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0), 0);
  const total = subtotal - Number(form.discount_amount || 0);

  // Min price check
  const belowMinRows = items.map((it, i) => {
    if (!it.product_id) return null;
    const p = products.find((x) => x.id === it.product_id);
    if (!p?.calculated_minimum_sales_price) return null;
    if (Number(it.unit_price) < Number(p.calculated_minimum_sales_price)) {
      return { idx: i, name: p.product_name, min: p.calculated_minimum_sales_price };
    }
    return null;
  }).filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0 || items.some((it) => !it.product_id && !it.manual_product_name)) {
      return toast.error("Tüm satırlarda ürün seçili veya manuel ürün adı girilmiş olmalı");
    }
    if (isOrder && items.some((it) => !it.product_id)) {
      return toast.error("Sipariş için tüm satırlarda kayıtlı ürün seçilmelidir");
    }
    setSaving(true);
    try {
      const payload = {
        company_id: form.company_id || null,
        manual_company_name: form.manual_company_name,
        quote_date: form.quote_date,
        valid_until: form.valid_until,
        order_date: form.order_date,
        contact_person: form.contact_person,
        phone: form.phone,
        email: form.email,
        contact_title: form.contact_title,
        delivery_time: form.delivery_time,
        payment_term_days: Number(form.payment_term_days),
        items: items.map((it) => ({
          product_id: it.product_id || null,
          manual_product_name: it.manual_product_name,
          product_code: it.product_code,
          quantity: Number(it.quantity),
          unit: it.unit,
          unit_price: Number(it.unit_price),
          currency: form.currency,
        })),
        notes: form.notes,
        discount_amount: Number(form.discount_amount || 0),
        status: form.status,
        currency: form.currency,
        quote_id: preQuote || null,
      };
      let result;
      if (isOrder) {
        result = await api.post("/orders", payload);
        toast.success("Sipariş oluşturuldu");
        navigate("/orders");
      } else if (isEdit) {
        result = await api.put(`/quotes/${id}`, payload);
        toast.success("Teklif güncellendi");
        navigate("/quotes");
      } else {
        result = await api.post("/quotes", payload);
        toast.success("Teklif oluşturuldu");
        navigate("/quotes");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="azd-card p-6">
        <div className="text-sm font-bold text-[#08285A] mb-4 uppercase tracking-wider">
          {isOrder ? "Sipariş" : "Teklif"} Bilgileri
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">
              Müşteri <span className="text-[#DC2626]">*</span>
            </label>
            <Select value={form.company_id || "none"} onValueChange={(v) => set("company_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="quote-company"><SelectValue placeholder="Firma seçin" /></SelectTrigger>
              <SelectContent>
                {!isOrder && <SelectItem value="none">— Manuel —</SelectItem>}
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!form.company_id && !isOrder && (
              <Input className="mt-2" placeholder="Manuel firma adı..."
                     value={form.manual_company_name}
                     onChange={(e) => set("manual_company_name", e.target.value)} />
            )}
          </div>
          {!isOrder ? (
            <div>
              <label className="block text-xs font-semibold text-[#111827] mb-1.5">Durum</label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Taslak</SelectItem>
                  <SelectItem value="sent">Gönderildi</SelectItem>
                  <SelectItem value="approved">Onaylandı</SelectItem>
                  <SelectItem value="rejected">Onaylanmadı</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : <div></div>}

          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">
              {isOrder ? "Sipariş Tarihi" : "Teklif Tarihi"} <span className="text-[#DC2626]">*</span>
            </label>
            <Input type="date" value={isOrder ? form.order_date : form.quote_date}
                   onChange={(e) => set(isOrder ? "order_date" : "quote_date", e.target.value)}
                   required data-testid="quote-date" />
          </div>
          {!isOrder && (
            <div>
              <label className="block text-xs font-semibold text-[#111827] mb-1.5">
                Geçerlilik Tarihi <span className="text-[#DC2626]">*</span>
              </label>
              <Input type="date" value={form.valid_until}
                     onChange={(e) => set("valid_until", e.target.value)} required />
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">
              Vade (Gün) <span className="text-[#DC2626]">*</span>
            </label>
            <Input type="number" min="0" value={form.payment_term_days}
                   onChange={(e) => set("payment_term_days", e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">
              Para Birimi <span className="text-[#DC2626]">*</span>
            </label>
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger data-testid="quote-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CURRENCY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">
              İlgili Kişi <span className="text-[#DC2626]">*</span>
            </label>
            <Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)}
                   required data-testid="quote-contact" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">
              Telefon <span className="text-[#DC2626]">*</span>
            </label>
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">
              E-Posta <span className="text-[#DC2626]">*</span>
            </label>
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">
              Görüşülen Ünvan <span className="text-[#DC2626]">*</span>
            </label>
            <Input value={form.contact_title} onChange={(e) => set("contact_title", e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">
              Teslimat Süresi <span className="text-[#DC2626]">*</span>
            </label>
            <Input value={form.delivery_time} onChange={(e) => set("delivery_time", e.target.value)} required />
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Açıklama</label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} />
          </div>
        </div>
      </div>

      {belowMinRows.length > 0 && (
        <div className="azd-card p-4 border-l-4 border-[#F59E0B] bg-[#FEF3C7]/30">
          <div className="flex gap-2">
            <AlertTriangle size={18} className="text-[#F59E0B] shrink-0 mt-0.5" />
            <div className="text-sm text-[#854D0E]">
              <div className="font-bold mb-1">Minimum Fiyat Uyarısı</div>
              {belowMinRows.map((r) => (
                <div key={r.idx} className="text-xs">
                  • {r.name}: Girdiğiniz fiyat minimum satış fiyatının ({fmtTL(r.min)}) altındadır.
                </div>
              ))}
              {user?.role === "sales" && (
                <div className="text-xs mt-1 italic">
                  Bu teklif kaydedildiğinde yöneticiye bildirim gönderilecektir.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="azd-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-bold text-[#08285A] uppercase tracking-wider">
            Ürün Satırları ({items.length})
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addRow}
                  data-testid="add-item-row">
            <Plus size={14} className="mr-1" /> Satır Ekle
          </Button>
        </div>
        <div className="overflow-x-auto azd-scrollbar">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FB] text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="text-left px-3 py-2 font-semibold w-[300px]">Ürün</th>
                <th className="text-left px-3 py-2 font-semibold">Kod</th>
                <th className="text-right px-3 py-2 font-semibold w-24">Miktar</th>
                <th className="text-left px-3 py-2 font-semibold w-28">Birim</th>
                <th className="text-right px-3 py-2 font-semibold w-32">Birim Fiyat</th>
                <th className="text-right px-3 py-2 font-semibold w-32">Toplam</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                const minPrice = it.product_id
                  ? products.find((p) => p.id === it.product_id)?.calculated_minimum_sales_price
                  : null;
                const below = minPrice && Number(it.unit_price) < Number(minPrice);
                return (
                  <tr key={i} className="border-t border-[#E5E7EB]" data-testid={`item-row-${i}`}>
                    <td className="px-2 py-2">
                      <Select value={it.product_id || "manual"}
                              onValueChange={(v) => setItem(i, "product_id", v === "manual" ? "" : v)}>
                        <SelectTrigger className="h-9 text-xs" data-testid={`item-product-${i}`}>
                          <SelectValue placeholder="Seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {!isOrder && <SelectItem value="manual">— Manuel —</SelectItem>}
                          {products.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.product_code} — {p.product_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!it.product_id && !isOrder && (
                        <Input className="mt-1 h-8 text-xs" placeholder="Manuel ürün adı..."
                               value={it.manual_product_name}
                               onChange={(e) => setItem(i, "manual_product_name", e.target.value)} />
                      )}
                    </td>
                    <td className="px-2 py-2 text-xs font-mono text-[#0B3676]">{it.product_code || "-"}</td>
                    <td className="px-2 py-2">
                      <Input type="number" min="0.01" step="0.01" value={it.quantity}
                             onChange={(e) => setItem(i, "quantity", e.target.value)}
                             className="h-9 text-right" data-testid={`item-qty-${i}`} />
                    </td>
                    <td className="px-2 py-2">
                      <Select value={it.unit} onValueChange={(v) => setItem(i, "unit", v)}>
                        <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-2 py-2">
                      <Input type="number" min="0" step="0.01" value={it.unit_price}
                             onChange={(e) => setItem(i, "unit_price", e.target.value)}
                             className={`h-9 text-right ${below ? "border-[#F59E0B] bg-[#FEF3C7]/30" : ""}`}
                             data-testid={`item-price-${i}`} />
                    </td>
                    <td className="px-2 py-2 text-right font-bold text-[#0B3676]">
                      {fmtMoney(Number(it.quantity) * Number(it.unit_price), form.currency)}
                    </td>
                    <td className="px-2 py-2">
                      {items.length > 1 && (
                        <button type="button" onClick={() => removeRow(i)}
                                data-testid={`remove-item-${i}`}
                                className="p-1.5 text-[#DC2626] hover:bg-[#DC2626]/10 rounded">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-5 flex flex-col md:flex-row md:justify-end">
          <div className="md:w-72 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#6B7280]">Ara Toplam:</span>
              <span className="font-semibold">{fmtMoney(subtotal, form.currency)}</span>
            </div>
            <div className="flex justify-between text-sm items-center gap-2">
              <span className="text-[#6B7280]">İskonto:</span>
              <Input type="number" min="0" step="0.01" value={form.discount_amount}
                     onChange={(e) => set("discount_amount", e.target.value)}
                     className="h-8 w-32 text-right" />
            </div>
            <div className="flex justify-between text-base font-extrabold bg-[#08285A] text-white px-3 py-2 rounded-lg">
              <span>GENEL TOPLAM:</span>
              <span>{fmtMoney(total, form.currency)}</span>
            </div>
            <div className="text-[11px] text-[#DC2626] font-bold text-right">
              Fiyatlarımıza KDV dahil değildir.
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-3 justify-end">
        <Button type="button" variant="outline" onClick={() => navigate(isOrder ? "/orders" : "/quotes")}>
          Vazgeç
        </Button>
        <Button type="submit" disabled={saving} data-testid="save-quote"
                className="bg-[#0B3676] hover:bg-[#08285A] text-white">
          {saving ? "Kaydediliyor..." : isOrder ? "Siparişi Oluştur" : isEdit ? "Güncelle" : "Teklifi Kaydet"}
        </Button>
      </div>
    </form>
  );
}
